import React, {useRef, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import Image from '~/src/components/image/Image';
import Play from '~/src/components/svg/general/Play';

import {classNames, getFormatClassNames} from '~/src/utilities/helpers/css';
import {TeaserTypes} from '~/src/utilities/constants/types';
import {TeaserFormat, ImageFormat, ColorScheme} from '~/src/utilities/constants/formats';
import {SectionLayout} from '~/src/utilities/constants/layout';
import {ContentStyle} from '~/src/utilities/constants/style';
import {ImageSize} from '~/src/utilities/constants/image';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {withSectionContext} from '~/src/components/context/SectionContext';
import {withSettingsContext} from '~/src/components/context/SettingsContext';

import css from './TeaserContentMedia.module.scss';

const getImageSizes = (format) => {
    switch (format) {
        case TeaserFormat.TEASER_FULL:
            return ImageSize.TEASER_FULL;
        case TeaserFormat.TEASER_LARGE:
            return ImageSize.TEASER_LARGE;
        default:
            return ImageSize.TEASER_SMALL;
    }
};

const getKickwordIconSrc = (kickword) => {
    return Object.values(get(kickword, 'icon.variants', {}))
        .map(({src}) => src)
        .pop();
};

const TeaserContentMedia = (props) => {
    const isBreakingNews = props.isBreakingNews;
    const siteArea = get(props, 'context.settings.siteArea', SiteArea.NEWS);
    const imageSizes = getImageSizes(props.format.primaryGrid);
    const kickword = isBreakingNews
        ? {backgroundColorCode: css.colorWhite, colorCode: css.colorWhite, name: 'breaking news'}
        : get(props, 'content.kickword', {});
    const kickwordIconSrc = getKickwordIconSrc(kickword);
    const {useEmbedUrl, embedUrl} = props.content;

    const isFridaySection = get(props, 'context.section.layout') === SectionLayout.FRIDAY || get(props, 'content.styles.name') === ContentStyle.FRIDAY;
    const isLematinSection = get(props, 'context.section.layout') === SectionLayout.LEMATIN || get(props, 'content.styles.name') === ContentStyle.LEMATIN;
    const isLifestyleSection = get(props, 'context.section.layout') === SectionLayout.LIFESTYLE || get(props, 'content.styles.name') === ContentStyle.LIFESTYLE;
    const isCarouselSection =
        get(props, 'context.section.layout') === SectionLayout.CAROUSEL || get(props, 'context.section.layout') === SectionLayout.CAROUSEL_VIDEO_SERIES;

    const className = classNames(css, 'media', getFormatClassNames(props.format), props.type, siteArea, {
        isFridaySection,
        isLematinSection,
        isLifestyleSection,
        isCarouselSection,
        isBreakingNews
    });

    const ref = useRef();

    const [showEmbed, setShowEmbed] = useState(false);

    if (useEmbedUrl) {
        useEffect(() => {
            if (!window.IntersectionObserver) {
                setShowEmbed(true);
                return;
            }

            const options = {
                rootMargin: '500px 0px 500px 0px',
                threshold: 0.0
            };

            const observer = new IntersectionObserver((entries) => {
                setShowEmbed(entries.length && entries[0].isIntersecting);
            }, options);

            observer.observe(ref.current);

            return () => observer.disconnect();
        });
    }

    return (
        <div className={className} ref={ref}>
            {(!isBreakingNews || props.content.image || useEmbedUrl) &&
                (!useEmbedUrl ? (
                    <div className={css.imgContainer}>
                        {props.content.image && <Image {...props.content.image} format={ImageFormat.IMAGE_COVER} sizes={imageSizes} hideTitle={true} />}
                    </div>
                ) : (
                    <div className={css.embedContainer}>
                        {showEmbed && <iframe className={css.embedIframe} src={embedUrl} frameBorder="0" scrolling="no" />}
                        <div className={css.embedCover}></div>
                    </div>
                ))}

            {props.type === TeaserTypes.VIDEOS && siteArea === SiteArea.NEWS && (
                <div className={css.playIcon}>
                    <Play />
                </div>
            )}

            {kickword.name && siteArea !== SiteArea.LIFESTYLE && (
                <h3
                    className={css.kickword}
                    style={{
                        backgroundColor: kickword.backgroundColorCode || kickword.colorCode,
                        color: kickword.textColorCode,
                        borderColor: kickword.borderColorCode,
                        borderStyle: kickword.borderColorCode ? 'solid' : 'none'
                    }}
                >
                    {kickwordIconSrc && <img className={css.kickwordIcon} src={kickwordIconSrc} alt={kickword.name} />}
                    {kickword.name}
                </h3>
            )}
        </div>
    );
};

if (process.env.NODE_ENV === 'development') {
    TeaserContentMedia.propTypes = {
        format: PropTypes.shape({
            primaryGrid: PropTypes.oneOf(Object.values(TeaserFormat)).isRequired,
            secondaryGrid: PropTypes.oneOf(Object.values(TeaserFormat)).isRequired,
            colorScheme: PropTypes.oneOf(Object.values(ColorScheme)).isRequired
        }),
        type: PropTypes.string,
        isBreakingNews: PropTypes.bool,
        content: PropTypes.shape({
            image: PropTypes.shape({
                id: PropTypes.string.isRequired,
                variants: PropTypes.objectOf(
                    PropTypes.shape({
                        src: PropTypes.string.isRequired,
                        width: PropTypes.number.isRequired,
                        height: PropTypes.number.isRequired
                    })
                )
            }),
            kickword: PropTypes.shape({
                name: PropTypes.string.isRequired,
                colorCode: PropTypes.string,
                backgroundColorCode: PropTypes.string,
                textColorCode: PropTypes.string,
                borderColorCode: PropTypes.string
            })
        })
    };
}

export default withSettingsContext(withSectionContext(TeaserContentMedia));
